import { AlertDialogProps, Button, ButtonProps, HStack, IconButton, Text, Tooltip } from "@chakra-ui/react";
import { ReactElement, useCallback, useRef, useState } from "react";
import { IoOpenOutline } from "react-icons/io5";

import { useIsMountedRef as useIsMountedReference } from "../../hooks";
import { DialogModal, DialogModalProperties } from "./DialogModal";

export interface DialogProperties
  extends Pick<
      ButtonProps,
      "aria-label" | "bg" | "colorScheme" | "isLoading" | "size" | "variant" | "width" | "tabIndex"
    >,
    Omit<DialogModalProperties, keyof AlertDialogProps> {
  inputComponent?: (properties: { isDisabled?: boolean; onClick: () => void }) => React.ReactElement;
  isDisabled?: boolean;
  label?: boolean;
  shouldOpenDialog?: () => boolean;
  showIcon?: boolean;
  showLabel?: boolean;
  disabledMessage?: string;
}

export const Dialog = ({
  "aria-label": ariaLabel = "Dialog open button",
  bg,
  buttonLabel = "Ouvrir",
  colorScheme = "info",
  icon = <IoOpenOutline />,
  inputComponent: InputComponent,
  isDisabled,
  isLoading,
  label = true,
  onConfirm,
  shouldOpenDialog,
  showIcon = false,
  showLabel = true,
  size = "md",
  variant = "outline",
  width,
  tabIndex,
  disabledMessage,
  ...rest
}: DialogProperties): ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const onClose = () => setIsOpen(false);
  const cancelReference = useRef<HTMLButtonElement>(null);
  const isMountedReference = useIsMountedReference();

  const handleConfirm = useCallback(async () => {
    await onConfirm?.();
    if (isMountedReference.current) {
      setIsOpen(false);
    }
  }, [isMountedReference, onConfirm]);

  const handleOnClick = useCallback(() => {
    setIsOpen(shouldOpenDialog?.() ?? true);
  }, [shouldOpenDialog]);

  return (
    <>
      {InputComponent == null ? (
        <Tooltip label={isDisabled ? disabledMessage : undefined}>
          {label ? (
            <Button
              aria-label={ariaLabel}
              bg={bg}
              colorScheme={colorScheme}
              data-testid="dialog-open-button"
              isDisabled={isDisabled}
              isLoading={isLoading}
              onClick={handleOnClick}
              size={size}
              variant={variant}
              width={width}
              tabIndex={tabIndex}
            >
              <HStack>
                {showIcon && icon}
                {showLabel && <Text>{buttonLabel}</Text>}
              </HStack>
            </Button>
          ) : (
            <IconButton
              aria-label={ariaLabel}
              colorScheme={colorScheme}
              data-testid="dialog-open-button"
              icon={icon}
              isDisabled={isDisabled}
              isLoading={isLoading}
              onClick={handleOnClick}
              size={size}
              tabIndex={tabIndex}
              variant="outline"
            />
          )}
        </Tooltip>
      ) : (
        <InputComponent isDisabled={isDisabled} onClick={handleOnClick} />
      )}
      <DialogModal
        {...rest}
        buttonLabel={buttonLabel}
        colorScheme={colorScheme}
        icon={icon}
        isLoading={isLoading}
        isOpen={isOpen}
        leastDestructiveRef={cancelReference}
        onClose={onClose}
        onConfirm={onConfirm ? handleConfirm : undefined}
      />
    </>
  );
};
